'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '../context/AuthContext';
import Link from 'next/link';
import Image from 'next/image';
import { ShieldCheck, Mail, Loader2, X, Info } from 'lucide-react';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const router = useRouter();

  // Forgot Password Modal States
  const [isForgotModalOpen, setIsForgotModalOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [isForgotLoading, setIsForgotLoading] = useState(false);

  const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const res = await fetch(`${authApi}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
        credentials: 'include',
      });

      if (res.ok) {
        const token = await res.text();
        login(token); 
      } else {
        const errorMsg = await res.text();
        setError(errorMsg || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error(err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');
    setIsForgotLoading(true);

    try {
      const res = await fetch(`${authApi}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emailOrUsername: forgotEmail.trim() }),
      });

      if (res.ok) {
        setForgotSuccess('Instructions to reset your password have been processed. Please check your email.');
        setForgotEmail('');
      } else {
        const errorMsg = await res.text();
        setForgotError(errorMsg || 'Failed to send reset link. User may not exist.');
      }
    } catch (err) {
      console.error(err);
      setForgotError('Connection failed. Please check your internet connection.');
    } finally {
      setIsForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-deep-green p-4 sm:p-8">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <Image 
          src="https://images.unsplash.com/photo-1606859191214-25806e8e2423?q=80&w=2000&auto=format&fit=crop" 
          alt="Fresh Organic Produce" 
          fill 
          className="object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
      </div>

      {/* Centered Login Card */}
      <div className="relative z-10 w-full max-w-md bg-white/95 backdrop-blur-md p-8 sm:p-10 rounded-3xl shadow-2xl border border-white/20">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex flex-col items-center justify-center group">
            <Image src="/images/pvlogo.png" alt="Pasumai Virumbi Logo" width={64} height={64} className="object-contain group-hover:scale-105 transition-transform duration-300 drop-shadow-md mb-2" />
            <h2 className="text-2xl font-display font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-deep-green to-mid-green">
              Pasumai Virumbi
            </h2>
            <div className="h-[2px] w-1/2 bg-gradient-to-r from-amber to-transparent rounded-full my-1" />
            <p className="text-[10px] font-bold text-text-muted uppercase tracking-[0.2em]">100% Pure & Organic</p>
          </Link>
        </div>

        <div className="mb-6 text-center">
          <h3 className="text-xl font-bold text-deep-green mb-1">Welcome Back</h3>
          <p className="text-text-muted text-sm font-medium">Please sign in to access your account.</p>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3.5 rounded-lg mb-6 text-sm font-medium shadow-sm flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-deep-green mb-1.5">Email Address</label>
            <input 
              type="email" 
              required
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none text-deep-green font-medium shadow-sm"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your email"
            />
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="block text-sm font-bold text-deep-green">Password</label>
              <button 
                type="button"
                onClick={() => setIsForgotModalOpen(true)}
                className="text-xs font-bold text-mid-green hover:text-deep-green hover:underline focus:outline-none"
              >
                Forgot password?
              </button>
            </div>
            <input 
              type="password" 
              required
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none text-deep-green font-medium shadow-sm"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
            />
          </div>

          <button 
            type="submit" 
            disabled={isLoading}
            className={`w-full bg-mid-green text-white py-3.5 rounded-xl font-bold hover:bg-deep-green transition-all shadow-lg shadow-mid-green/20 mt-4 flex justify-center items-center ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <p className="mt-8 text-center text-sm font-medium text-text-muted">
          Don't have an account?{' '}
          <Link href="/register" className="text-amber font-bold hover:text-deep-green transition-colors hover:underline">
            Create an account
          </Link>
        </p>
      </div>

      {/* Forgot Password Modal */}
      {isForgotModalOpen && (
        <div className="fixed inset-0 bg-black/65 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
          <div className="absolute inset-0" onClick={() => setIsForgotModalOpen(false)} />
          
          <div className="bg-white w-full max-w-md rounded-3xl p-6 sm:p-8 shadow-2xl relative z-10 border border-gray-100 animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center pb-4 mb-6 border-b border-gray-100">
              <h3 className="font-display font-black text-lg text-deep-green uppercase tracking-wide">Forgot Password</h3>
              <button 
                type="button"
                onClick={() => setIsForgotModalOpen(false)}
                className="p-1.5 rounded-full hover:bg-gray-50 border border-gray-200 transition-colors"
              >
                <X className="w-4 h-4 text-deep-green" />
              </button>
            </div>

            {forgotError && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3.5 rounded-lg mb-5 text-xs font-semibold flex items-start gap-2.5">
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                {forgotError}
              </div>
            )}

            {forgotSuccess && (
              <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-3.5 rounded-lg mb-5 text-xs font-semibold flex items-start gap-2.5">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                {forgotSuccess}
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Username or Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-text-muted" />
                  <input 
                    type="text" 
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Enter your registered username or email"
                    className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none rounded-xl font-medium text-sm text-deep-green"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
                <button
                  type="button"
                  onClick={() => setIsForgotModalOpen(false)}
                  className="px-5 py-2.5 bg-pale-green hover:bg-amber hover:text-white text-deep-green font-bold rounded-xl text-xs uppercase tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isForgotLoading || !!forgotSuccess}
                  className="bg-mid-green hover:bg-deep-green text-white font-bold px-6 py-2.5 rounded-xl transition-all text-xs uppercase tracking-wider shadow-md hover:shadow-lg flex items-center gap-2"
                >
                  {isForgotLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                    </>
                  ) : (
                    'Request Reset'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
